import React, { createContext, useCallback, useContext, useState } from "react";
import axios, { AxiosError } from "axios";
import { useAuth } from "@context/auth";
import { getCookie } from "@utils/utilsCookie";
import { useTranslation } from "react-i18next";

const apiTimeout = 30000;

const buildApi = (authToken?: string) =>
  axios.create({
    timeout: apiTimeout,
    headers: {
      Authorization: `Bearer ${authToken ?? ""}`,
      "x-ada-session-token": getCookie("sessionId"),
    },
  });

export type AgentType = "admin" | "support";

export const AGENT_BASE_URLS: Record<AgentType, string | undefined> = {
  admin: process.env.REACT_APP_AGENT_AI_ADMIN,
  support: process.env.REACT_APP_AGENT_AI_SUPPORT,
};

export const AGENT_API_PREFIX: Record<AgentType, string> = {
  admin: "learning_agent/admin",
  support: "helpdesk_agent",
};

export const agentSupportsHistory = (type: AgentType | null): boolean =>
  type === "support" || type === "admin";

const generateSessionId = (prefix = ""): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${prefix}${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const MERMAID_BLOCK_RE = /```\s*mermaid[\s\S]*?```/gi;

export const buildConversationPreview = (
  text: string | undefined,
  diagramLabel: string,
): string => {
  if (!text) return "";
  const stripped = text
    .replace(MERMAID_BLOCK_RE, " ")
    .replace(/\s+/g, " ")
    .trim();
  const original = text.replace(/\s+/g, " ").trim();
  const hadMermaid = stripped !== original;
  if (!hadMermaid) return text.slice(0, 80);
  const tag = `📊 ${diagramLabel}`;
  return (stripped ? `${tag} — ${stripped}` : tag).slice(0, 80);
};

const ensureTrailingSlash = (url?: string) =>
  !url ? "" : url.endsWith("/") ? url : `${url}/`;

export const buildAgentUrl = (
  type: AgentType | null,
  path: string,
): string | undefined => {
  if (!type) return undefined;
  const base = AGENT_BASE_URLS[type];
  if (!base) return undefined;
  return `${ensureTrailingSlash(base)}${AGENT_API_PREFIX[type]}/${path}`;
};

export interface ConversationSummary {
  session_id: string;
  message_count?: number;
  created_at?: string;
  updated_at?: string;
  title?: string;
  preview?: string;
}

interface ChatAgentContextType {
  isOpen: boolean;
  sessionId: string;
  corporateId: string;
  agentType: AgentType | null;
  selectAgentType: (type: AgentType) => Promise<void>;
  conversations: ConversationSummary[];
  isLoadingConversations: boolean;
  isCreatingConversation: boolean;
  isFreshSession: boolean;
  clearFreshSession: () => void;
  freshSessionId: string;
  providerError: string;
  clearProviderError: () => void;
  openChat: (corporateId: string) => void;
  closeChat: () => void;
  selectConversation: (sessionId: string) => void;
  newConversation: () => Promise<string | null>;
  deleteConversation: (sessionId: string) => Promise<void>;
  refreshConversations: () => Promise<void>;
  updateConversationMeta: (
    sessionId: string,
    patch: Partial<ConversationSummary>,
  ) => void;
  bumpConversationToTop: (
    sessionId: string,
    patch?: Partial<ConversationSummary>,
  ) => void;
  removeConversationLocal: (sessionId: string) => void;
}

const ChatAgentContext = createContext<ChatAgentContextType | undefined>(
  undefined,
);

export const useChatAgent = (): ChatAgentContextType => {
  const context = useContext(ChatAgentContext);
  if (!context) {
    throw new Error(
      "useChatAgent deve essere usato all'interno di un ChatAgentProvider",
    );
  }
  return context;
};

export const ChatAgentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { t } = useTranslation("agent_chat");
  const diagramLabel = t("previewDiagram") || "Diagram";
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState("");
  const [storedCorporateId, setStoredCorporateId] = useState("");
  const [agentType, setAgentType] = useState<AgentType | null>(null);
  // Sessione dell'agente Amministrazione (senza storico): si conserva
  // tra open/close della modale nello stesso tab, non sopravvive al refresh.
  const [persistedAdminSessionId, setPersistedAdminSessionId] = useState<
    Record<string, string>
  >({});
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const [isFreshSession, setIsFreshSession] = useState(false);
  // Sessione appena avviata: tiene visibile in chat l'opzione della scelta
  // agente finché l'utente non passa a un'altra conversazione.
  const [freshSessionId, setFreshSessionId] = useState("");
  const [providerError, setProviderError] = useState("");

  const clearProviderError = useCallback(() => setProviderError(""), []);
  const clearFreshSession = useCallback(() => setIsFreshSession(false), []);
  const userId = user?.id_user?.toString() ?? "";

  const fetchSessionInfo = async (
    sid: string,
    corporateId: string,
    agentType: AgentType,
  ): Promise<Partial<ConversationSummary>> => {
    const endpoint = buildAgentUrl(agentType, `messages/${sid}`);
    if (!endpoint) return {};
    try {
      const res = await buildApi(user?.authToken).get(endpoint, {
        params: { corporate_id: corporateId },
      });
      const items: Array<{ role?: string; text?: string; timestamp?: string }> =
        res.data?.messages ?? [];
      if (items.length === 0) {
        return { message_count: 0 };
      }
      const firstUser = items.find((m) => m.role === "user");
      const lastAssistant = [...items].reverse().find((m) => m.role !== "user");
      const first = items[0];
      const last = items[items.length - 1];
      return {
        message_count: items.length,
        title: firstUser?.text?.slice(0, 60),
        preview: buildConversationPreview(lastAssistant?.text, diagramLabel),
        created_at: first?.timestamp,
        updated_at: last?.timestamp,
      };
    } catch (error) {
      const status = (error as AxiosError)?.response?.status;
      if (status === 404) {
        return { message_count: 0 };
      }
      console.error("[ChatAgent] Failed to fetch session messages", sid, error);
      return {};
    }
  };

  const refreshConversationsFor = useCallback(
    async (corporateId: string, agentType: AgentType) => {
      if (!userId || !corporateId) return;

      const endpoint = buildAgentUrl(agentType, `sessions/${userId}`);

      if (!endpoint) {
        setProviderError("agentNotConfigured");
        return;
      }

      setIsLoadingConversations(true);
      try {
        const res = await buildApi(user?.authToken).get(endpoint, {
          params: { corporate_id: corporateId },
        });
        let sessionIds: string[] = [];

        if (agentType === "admin") {
          sessionIds = res.data?.sessions.map((session: any) => {
            return session.session_id;
          });
        }
        if (agentType === "support") {
          sessionIds = res.data?.session_ids ?? [];
        }

        const infos = await Promise.all(
          sessionIds.map((id) => fetchSessionInfo(id, corporateId, agentType)),
        );
        const list: ConversationSummary[] = sessionIds
          .map((id, i) => ({
            session_id: id,
            ...infos[i],
          }))
          .filter((c) => c.message_count !== 0);
        list.sort((a, b) => {
          const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
          return tb - ta;
        });
        setConversations(list);
      } catch (error) {
        console.error("[ChatAgent] Failed to fetch conversations", error);
        setProviderError("conversationsListFailed");
      } finally {
        setIsLoadingConversations(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, user?.authToken],
  );

  const refreshConversations = useCallback(async () => {
    agentType &&
      storedCorporateId &&
      (await refreshConversationsFor(storedCorporateId, agentType));
  }, [refreshConversationsFor, storedCorporateId]);

  const createConversation = async (
    type: AgentType,
  ): Promise<string | null> => {
    const endpoint = buildAgentUrl(type, "conversations");
    if (!endpoint) {
      setProviderError("agentNotConfigured");
      return null;
    }
    setIsCreatingConversation(true);
    try {
      const res = await buildApi(user?.authToken).post(endpoint);
      const newSid: string | undefined = res.data?.session_id;
      if (!newSid) {
        setProviderError("createConversationFailed");
        return null;
      }
      return newSid;
    } catch (error) {
      console.error("[ChatAgent] Failed to create conversation", error);
      setProviderError("createConversationFailed");
      return null;
    } finally {
      setIsCreatingConversation(false);
    }
  };

  const openChat = (corporateId: string) => {
    setIsOpen(true);
    setStoredCorporateId(corporateId);
    setAgentType(null);
    setSessionId("");
    setConversations([]);
    setIsFreshSession(false);
    setFreshSessionId("");
    setProviderError("");
  };

  const handleNewSid = (type: AgentType) => {
    if (type === "support") {
      return createConversation(type) ?? "";
    }
    return crypto.randomUUID();
  };

  const selectAgentType = useCallback(
    async (type: AgentType) => {
      if (!AGENT_BASE_URLS[type]) {
        setProviderError("agentNotConfigured");
        return;
      }
      setAgentType(type);
      setIsFreshSession(true);
      refreshConversationsFor(storedCorporateId, type);

      handleNewSid(type);
      const newSid = await handleNewSid(type);

      if (newSid) {
        setSessionId(newSid);
        setFreshSessionId(newSid);
      }
      return;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      storedCorporateId,
      refreshConversationsFor,
      user?.authToken,
      persistedAdminSessionId,
    ],
  );

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const selectConversation = useCallback((sid: string) => {
    setIsFreshSession(false);
    setFreshSessionId("");
    setSessionId(sid);
  }, []);

  const newConversation = useCallback(async (): Promise<string | null> => {
    setSessionId("");
    setAgentType(null);
    setIsFreshSession(false);
    setFreshSessionId("");
    setProviderError("");
    setPersistedAdminSessionId({});
    return null;
  }, []);

  const deleteConversation = useCallback(
    async (sid: string) => {
      const endpoint = buildAgentUrl(agentType, `sessions/${sid}`);
      if (!endpoint) return;
      const deletedItem = conversations.find((c) => c.session_id === sid);
      const wasActive = sid === sessionId;
      setConversations((prev) => prev.filter((c) => c.session_id !== sid));
      if (wasActive && agentType) {
        setSessionId("");
        setIsFreshSession(true);
        setFreshSessionId("");
        createConversation(agentType).then((newSid) => {
          if (newSid) {
            setSessionId(newSid);
            setFreshSessionId(newSid);
          }
        });
      }
      try {
        await buildApi(user?.authToken).delete(endpoint, {
          params: { corporate_id: storedCorporateId },
        });
      } catch (error) {
        console.error("[ChatAgent] Failed to delete conversation", error);
        if (deletedItem) {
          setConversations((prev) => {
            if (prev.some((c) => c.session_id === sid)) return prev;
            return [...prev, deletedItem].sort((a, b) => {
              const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
              const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
              return tb - ta;
            });
          });
        }
        setProviderError("deleteConversationFailed");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.authToken, storedCorporateId, sessionId, conversations],
  );

  const updateConversationMeta = useCallback(
    (sid: string, patch: Partial<ConversationSummary>) => {
      setConversations((prev) =>
        prev.map((c) => (c.session_id === sid ? { ...c, ...patch } : c)),
      );
    },
    [],
  );

  const removeConversationLocal = useCallback((sid: string) => {
    setConversations((prev) => prev.filter((c) => c.session_id !== sid));
  }, []);

  const bumpConversationToTop = useCallback(
    (sid: string, patch?: Partial<ConversationSummary>) => {
      setConversations((prev) => {
        const now = new Date().toISOString();
        const merged: Partial<ConversationSummary> = {
          updated_at: now,
          ...patch,
        };
        const exists = prev.some((c) => c.session_id === sid);
        const next = exists
          ? prev.map((c) => (c.session_id === sid ? { ...c, ...merged } : c))
          : [
              {
                session_id: sid,
                created_at: now,
                ...merged,
              } as ConversationSummary,
              ...prev,
            ];
        return [...next].sort((a, b) => {
          const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
          return tb - ta;
        });
      });
    },
    [],
  );

  return (
    <ChatAgentContext.Provider
      value={{
        isOpen,
        sessionId,
        corporateId: storedCorporateId,
        agentType,
        selectAgentType,
        conversations,
        isLoadingConversations,
        isCreatingConversation,
        isFreshSession,
        clearFreshSession,
        freshSessionId,
        providerError,
        clearProviderError,
        openChat,
        closeChat,
        selectConversation,
        newConversation,
        deleteConversation,
        refreshConversations,
        updateConversationMeta,
        bumpConversationToTop,
        removeConversationLocal,
      }}
    >
      {children}
    </ChatAgentContext.Provider>
  );
};
